import { useIntl } from "react-intl";

import { FeaturedInfoCard } from "@/components/adg4/featured-info-card/src/featured-info-card";
import { IconSvgProps } from "@/libraries/wpl/foundations/src/icons/types";

import { useLocalizedImage } from "../../use-localized-image";

import { FeaturedInfoSection, REPORT } from "./featured-info";

const BarChartIcon = ({ size }: IconSvgProps) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 25"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M17.1429 0.518066V18.5181H23.1429V0.518066H17.1429Z"
            fill="#1868DB"
        />
        <path d="M0 24.5181H23.1429V21.0895H0V24.5181Z" fill="#1868DB" />
        <path d="M0 18.5181V9.08949H6L6 18.5181H0Z" fill="#1868DB" />
        <path
            d="M8.57143 5.66092V18.5181H14.5714V5.66092L8.57143 5.66092Z"
            fill="#1868DB"
        />
    </svg>
);

const PauseIcon = ({ size }: IconSvgProps) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 25"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M0 0.518066H7.86207V18.725H0V0.518066Z" fill="#1868DB" />
        <path
            d="M10.3448 18.725V0.518066H18.2069V18.725H10.3448Z"
            fill="#1868DB"
        />
        <path
            d="M20.6897 7.96634V21.2077H7.44828V24.5181H24V7.96634H20.6897Z"
            fill="#1868DB"
        />
    </svg>
);

const TargetIcon = ({ size }: IconSvgProps) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 25"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M0 9.94669L8.2428e-07 0.518066L9.42863 0.518066V3.94663L3.42856 3.94663L3.42856 9.94669H0Z"
            fill="#1868DB"
        />
        <path
            d="M17.1429 12.5181C17.1429 15.3584 14.8404 17.661 12 17.661C9.15973 17.661 6.8572 15.3584 6.8572 12.5181C6.8572 9.6778 9.15973 7.37528 12 7.37528C14.8404 7.37528 17.1429 9.6778 17.1429 12.5181Z"
            fill="#1868DB"
        />
        <path
            d="M24 24.5181V15.0895H20.5714V21.0895H14.5715V24.5181H24Z"
            fill="#1868DB"
        />
        <path
            d="M14.5715 0.518068L24 0.518068V9.94669H20.5714V3.94663H14.5715V0.518068Z"
            fill="#1868DB"
        />
        <path
            d="M8.66478e-05 24.5181H9.42862L9.42862 21.0895H3.42865V15.0895H8.66478e-05V24.5181Z"
            fill="#1868DB"
        />
    </svg>
);

const LayoutIcon = ({ size }: IconSvgProps) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 25"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M6.75 3.51807H0V10.2681L6.75 10.2681V3.51807Z"
            fill="#1868DB"
        />
        <path d="M9 3.51807V10.2681L24 10.2681V3.51807H9Z" fill="#1868DB" />
        <path d="M24 12.5181L0 12.5181V21.5181H24V12.5181Z" fill="#1868DB" />
    </svg>
);

export const ReportSection = () => {
    const intl = useIntl();
    return (
        <FeaturedInfoSection
            variant={REPORT}
            eyebrow={intl.formatMessage({
                id: "JiraSoftwareFeaturesPageV2.TabsSection.Report.Label",
                defaultMessage: "Report"
            })}
            header={intl.formatMessage({
                id: "JiraSoftwareFeaturesPageV2.HighlightReportSection.Header",
                defaultMessage: "Results, not roadblocks"
            })}
            description={intl.formatMessage({
                id: "JiraSoftwareFeaturesPageV2.HighlightReportSection.Description",
                defaultMessage:
                    "Out-of-the-box reports, real-time dashboards, and contextual insights make data-driven decisions your team’s new normal."
            })}
        >
            <FeaturedInfoCard
                image={{
                    image: {
                        src: useLocalizedImage("reportPlanYourWorkImage"),
                        alt: intl.formatMessage({
                            id: "JiraSoftwareFeaturesPageV2.HighlightReportSection.Card.AsYouPlan.ImageAlt",
                            defaultMessage: "Team capacity planning in Jira"
                        })
                    }
                }}
                Icon={BarChartIcon}
                title={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.HighlightReportSection.Card.AsYouPlan.Header",
                    defaultMessage: "As you plan your work"
                })}
                body={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.HighlightReportSection.Card.AsYouPlan.Description",
                    defaultMessage:
                        "Get a real-time view into the team’s workload and balance the work to keep plans on track."
                })}
            />
            <FeaturedInfoCard
                image={{
                    image: {
                        src: useLocalizedImage("reportDuringYourProjectImage"),
                        alt: intl.formatMessage({
                            id: "JiraSoftwareFeaturesPageV2.HighlightReportSection.Card.Project.ImageAlt",
                            defaultMessage:
                                "Sprint progress contextual insight on the Jira board"
                        })
                    }
                }}
                Icon={PauseIcon}
                title={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.HighlightReportSection.Card.Project.Header",
                    defaultMessage: "During your project"
                })}
                body={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.HighlightReportSection.Card.Project.Description",
                    defaultMessage:
                        "Stay on target to meet your goals with contextual insights right in the board view. Raise any blockers and troubleshoot concerns."
                })}
            />
            <FeaturedInfoCard
                image={{
                    image: {
                        src: useLocalizedImage("reportWhenYouLaunchImage"),
                        alt: intl.formatMessage({
                            id: "JiraSoftwareFeaturesPageV2.HighlightReportSection.Card.Launch.ImageAlt",
                            defaultMessage: "Deployment insights"
                        })
                    }
                }}
                Icon={TargetIcon}
                title={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.HighlightReportSection.Card.Launch.Header",
                    defaultMessage: "When you launch"
                })}
                body={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.HighlightReportSection.Card.Launch.Description",
                    defaultMessage:
                        "Create custom views to track tasks that support your go-to-market strategy, and optimize your delivery pipeline with deployment frequency and cycle time insights."
                })}
            />
            <FeaturedInfoCard
                image={{
                    image: {
                        src: useLocalizedImage("reportBringItAllTogetherImage"),
                        alt: intl.formatMessage({
                            id: "JiraSoftwareFeaturesPageV2.HighlightReportSection.Card.Dashboards.ImageAlt",
                            defaultMessage:
                                "Jira dashboard showing progress to goal"
                        })
                    }
                }}
                Icon={LayoutIcon}
                title={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.HighlightReportSection.Card.Dashboards.Header",
                    defaultMessage: "Bring it all together with dashboards"
                })}
                body={intl.formatMessage({
                    id: "JiraSoftwareFeaturesPageV2.HighlightReportSection.Card.Dashboards.Description",
                    defaultMessage:
                        "Track the metrics that matter most to your team. With dozens of built-in gadgets, easily customize dashboards for teams, stakeholders, and leadership."
                })}
            />
        </FeaturedInfoSection>
    );
};
